import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import MyStreamWaveDesktopTabs from "../MyStreamWaveDesktopTabs";
import { useContentTab } from "@/components/brain/ContentTabContext";
import { useSidebarState } from "../../../../hooks/useSidebarState";
import { ChevronDoubleLeftIcon, ArrowLeftIcon, MagnifyingGlassIcon, ChatBubbleLeftIcon, Squares2X2Icon, ShareIcon, LinkIcon, CheckIcon } from "@heroicons/react/24/outline";
import WavePicture from "../../../waves/WavePicture";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { createBreakpoint } from "react-use";
import WaveDropsSearchModal from "@/components/waves/drops/search/WaveDropsSearchModal";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveShareCopyAction } from "@/hooks/waves/useWaveShareCopyAction";
import WaveDescriptionPopover from "@/components/waves/header/WaveDescriptionPopover";
import { getWaveDescriptionPreviewText } from "@/helpers/waves/waveDescriptionPreview";
const useBreakpoint = createBreakpoint({
  LG: 1024,
  MD: 768,
  S: 0
});
interface MyStreamWaveTabsDefaultProps {
  readonly wave: ApiWave;
  readonly viewMode: WaveViewMode;
  readonly onToggleViewMode: () => void;
  readonly showGalleryToggle: boolean;
}
const MyStreamWaveTabsDefault: React.FC<MyStreamWaveTabsDefaultProps> = t0 => {
  const $ = _c(76);
  const {
    wave,
    viewMode,
    onToggleViewMode,
    showGalleryToggle
  } = t0;
  const {
    activeContentTab,
    setActiveContentTab
  } = useContentTab();
  const {
    toggleRightSidebar,
    isRightSidebarOpen
  } = useSidebarState();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const breakpoint = useBreakpoint();
  const showBackButton = breakpoint !== "LG";
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const waveChatScroll = useWaveChatScrollOptional();
  const isDirectMessage = wave.chat.scope.group?.is_direct_message ?? false;
  const showShareAction = !isDirectMessage;
  let t1;
  if ($[0] !== wave) {
    t1 = getWaveDescriptionPreviewText(wave);
    $[0] = wave;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewText = t1;
  const showDescriptionPreview = showShareAction && !!previewText;
  let t2;
  if ($[2] !== isDirectMessage || $[3] !== wave.id || $[4] !== wave.name) {
    t2 = {
      waveId: wave.id,
      waveName: wave.name,
      isDirectMessage
    };
    $[2] = isDirectMessage;
    $[3] = wave.id;
    $[4] = wave.name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    mode: waveLinkActionMode,
    label: waveLinkActionLabel,
    feedbackState: waveLinkActionFeedbackState,
    onClick: handleWaveLinkActionClick
  } = useWaveShareCopyAction(t2);
  let t3;
  if ($[6] !== isApp || $[7] !== router || $[8] !== searchParams || $[9] !== wave.chat.scope.group?.is_direct_message) {
    t3 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      params.delete("serialNo");
      params.delete("divider");
      params.delete("drop");
      const basePath = getWaveHomeRoute({
        isDirectMessage: wave.chat.scope.group?.is_direct_message ?? false,
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[6] = isApp;
    $[7] = router;
    $[8] = searchParams;
    $[9] = wave.chat.scope.group?.is_direct_message;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleMobileBack = t3;
  let t4;
  if ($[11] !== pathname || $[12] !== router || $[13] !== searchParams || $[14] !== setActiveContentTab || $[15] !== wave.id || $[16] !== waveChatScroll) {
    t4 = serialNo => {
      setActiveContentTab(MyStreamWaveTab.CHAT);
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("serialNo", String(serialNo));
      router.replace(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[11] = pathname;
    $[12] = router;
    $[13] = searchParams;
    $[14] = setActiveContentTab;
    $[15] = wave.id;
    $[16] = waveChatScroll;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleSearchSelect = t4;
  const waveLinkActionIconColor = waveLinkActionFeedbackState === "idle" ? "tw-text-iron-200" : "tw-text-emerald-300";
  let t5;
  if ($[18] !== waveLinkActionFeedbackState || $[19] !== waveLinkActionMode) {
    t5 = () => {
      if (waveLinkActionFeedbackState !== "idle") {
        return <CheckIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      if (waveLinkActionMode === "share") {
        return <ShareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      return <LinkIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    };
    $[18] = waveLinkActionFeedbackState;
    $[19] = waveLinkActionMode;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const renderWaveLinkActionIcon = t5;
  let t6;
  if ($[21] !== handleMobileBack || $[22] !== showBackButton) {
    t6 = showBackButton && <button onClick={handleMobileBack} className="-tw-ml-2.5 tw-mr-1.5 tw-flex tw-h-full tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-px-2.5 tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50" aria-label="Go back"><ArrowLeftIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></button>;
    $[21] = handleMobileBack;
    $[22] = showBackButton;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const t7 = wave.name;
  const t8 = wave.picture;
  let t9;
  if ($[24] !== wave.contributors_overview) {
    t9 = wave.contributors_overview.map(_temp);
    $[24] = wave.contributors_overview;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t9 || $[27] !== wave.name || $[28] !== wave.picture) {
    t10 = <div className="tw-size-9 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950"><WavePicture name={t7} picture={t8} contributors={t9} /></div>;
    $[26] = t9;
    $[27] = wave.name;
    $[28] = wave.picture;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== previewText || $[31] !== showDescriptionPreview || $[32] !== wave) {
    t11 = showDescriptionPreview ? <WaveDescriptionPopover wave={wave} align="left" ariaLabel="Show wave description" triggerClassName="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"><h1 className="tw-mb-0 tw-w-full tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1><span className="tw-mt-0.5 tw-block tw-w-full tw-truncate tw-text-xs tw-font-normal tw-text-iron-400">{previewText}</span></WaveDescriptionPopover> : <h1 className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1>;
    $[30] = previewText;
    $[31] = showDescriptionPreview;
    $[32] = wave;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t6) {
    t12 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-x-3">{t6}{t10}{t11}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t6;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== onToggleViewMode || $[39] !== showGalleryToggle || $[40] !== viewMode) {
    t13 = showGalleryToggle && <button type="button" onClick={onToggleViewMode} aria-label={viewMode === "chat" ? "Switch to gallery view" : "Switch to chat view"} className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{viewMode === "chat" ? <Squares2X2Icon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /> : <ChatBubbleLeftIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />}</button>;
    $[38] = onToggleViewMode;
    $[39] = showGalleryToggle;
    $[40] = viewMode;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== handleWaveLinkActionClick || $[43] !== renderWaveLinkActionIcon || $[44] !== showShareAction || $[45] !== waveLinkActionIconColor || $[46] !== waveLinkActionLabel || $[47] !== waveLinkActionMode) {
    t14 = showShareAction && <button type="button" onClick={handleWaveLinkActionClick} aria-label={waveLinkActionLabel} title={waveLinkActionLabel} data-wave-link-action-mode={waveLinkActionMode} className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white ${waveLinkActionIconColor}`}>{renderWaveLinkActionIcon()}</button>;
    $[42] = handleWaveLinkActionClick;
    $[43] = renderWaveLinkActionIcon;
    $[44] = showShareAction;
    $[45] = waveLinkActionIconColor;
    $[46] = waveLinkActionLabel;
    $[47] = waveLinkActionMode;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsSearchOpen(true);
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <button type="button" onClick={t15} aria-label="Search messages in this wave" className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white"><MagnifyingGlassIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /></button>;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  const t17 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition tw-duration-300 ${isRightSidebarOpen ? "tw-rotate-180 desktop-hover:group-hover:tw-translate-x-0.5" : "tw-rotate-0 desktop-hover:group-hover:-tw-translate-x-0.5"}`;
  let t18;
  if ($[51] !== t17) {
    t18 = <ChevronDoubleLeftIcon strokeWidth={2} className={t17} />;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t18 || $[54] !== toggleRightSidebar) {
    t19 = <button type="button" onClick={toggleRightSidebar} className="tw-group tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/80 desktop-hover:hover:tw-bg-iron-700/85 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]" aria-label="Toggle right sidebar">{t18}</button>;
    $[53] = t18;
    $[54] = toggleRightSidebar;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t13 || $[57] !== t14 || $[58] !== t19) {
    t20 = <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-x-2 tw-self-stretch">{t13}{t14}{t16}{t19}</div>;
    $[56] = t13;
    $[57] = t14;
    $[58] = t19;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== t12 || $[61] !== t20) {
    t21 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-px-2 tw-py-3 sm:tw-px-4">{t12}{t20}</div>;
    $[60] = t12;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== activeContentTab || $[64] !== setActiveContentTab || $[65] !== wave) {
    t22 = <div className="tw-border-b tw-border-l-0 tw-border-t-0 tw-border-solid tw-border-iron-800"><MyStreamWaveDesktopTabs activeTab={activeContentTab} wave={wave} setActiveTab={setActiveContentTab} /></div>;
    $[63] = activeContentTab;
    $[64] = setActiveContentTab;
    $[65] = wave;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => setIsSearchOpen(false);
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== handleSearchSelect || $[69] !== isSearchOpen || $[70] !== wave) {
    t24 = <WaveDropsSearchModal isOpen={isSearchOpen} onClose={t23} wave={wave} onSelectSerialNo={handleSearchSelect} />;
    $[68] = handleSearchSelect;
    $[69] = isSearchOpen;
    $[70] = wave;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== t21 || $[73] !== t22 || $[74] !== t24) {
    t25 = <div className="tw-flex tw-w-full tw-flex-col tw-bg-iron-950">{t21}{t22}{t24}</div>;
    $[72] = t21;
    $[73] = t22;
    $[74] = t24;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  return t25;
};
export default MyStreamWaveTabsDefault;
function _temp(c) {
  return {
    pfp: c.contributor_pfp,
    identity: c.contributor_identity
  };
}